/**
 * The player's one `<video>`: play, seek, speed, sound, the A-B loop and a single-frame step.
 *
 * The clock itself is NOT React state here — a playing film would re-render the whole player sixty
 * times a second. Each element gets one clock store, fed from the video frame callback, and
 * `useVideoClock` reads it for the few components that print it.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { preferenceStore } from '~/shared/lib/preferenceStore';

import {
  clampTime,
  FALLBACK_FRAME_SECONDS,
  frameSeconds,
  loopedTime,
  loopFrom,
  loopTo,
  NO_LOOP,
  stepRate,
  type LoopRange,
  type PlaybackRate,
} from '../model/playback';

const SOUND_KEY = 'movebuddy.player.sound';

/** The sound follows the trainer's last choice; a first visit starts muted. */
function soundWanted(): boolean {
  try {
    return preferenceStore()?.getItem(SOUND_KEY) === 'on';
  } catch {
    return false;
  }
}

function rememberSound(on: boolean): void {
  try {
    preferenceStore()?.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    // A refused store keeps the choice for this visit only, which is all it can do.
  }
}

/** One film's clock: the time it last reported, and a way to hear when that changes. */
export interface VideoClock {
  readonly now: () => number;
  readonly subscribe: (listener: () => void) => () => void;
}

interface ClockSource extends VideoClock {
  readonly set: (seconds: number) => void;
}

// Keyed by the element, so attaching the same element again hands back the same store.
const clocks = new WeakMap<HTMLVideoElement, ClockSource>();

function clockOf(video: HTMLVideoElement): ClockSource {
  const known = clocks.get(video);
  if (known !== undefined) return known;
  let seconds = video.currentTime;
  const listeners = new Set<() => void>();
  const made: ClockSource = {
    now: () => seconds,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set: (next) => {
      if (next === seconds) return;
      seconds = next;
      for (const listener of listeners) listener();
    },
  };
  clocks.set(video, made);
  return made;
}

export interface ClipPlayback {
  readonly attach: (element: HTMLVideoElement | null) => void;
  /** The attached film's clock, or `null` before one is attached. */
  readonly clock: VideoClock | null;
  /** The film's exact time now, for a command that acts on it. */
  readonly currentTime: () => number;
  readonly playing: boolean;
  readonly rate: PlaybackRate;
  readonly muted: boolean;
  readonly duration: number;
  readonly loop: LoopRange;
  readonly frameSeconds: () => number;
  readonly togglePlay: () => void;
  readonly pause: () => void;
  readonly seekTo: (seconds: number) => void;
  readonly seekBy: (seconds: number) => void;
  readonly frame: (by: 1 | -1) => void;
  readonly setRate: (rate: PlaybackRate) => void;
  readonly stepRate: (by: 1 | -1) => void;
  readonly toggleMute: () => void;
  readonly volumeBy: (delta: number) => void;
  readonly loopFrom: () => void;
  readonly loopTo: () => void;
  readonly loopClear: () => void;
}

export interface PlaybackStart {
  /** The full player plays a film as it arrives; the side panel's waits for the trainer. */
  readonly autoplay: boolean;
  /** The second the first film opens at, or `null` for the top. Later films start at the top. */
  readonly at: number | null;
}

/** @param onFrame called for every frame presented; the seen mark decides when that means a post. */
export function useClipPlayback(onFrame: () => void, start: PlaybackStart): ClipPlayback {
  // The node is written through the ref; the clock in state is what re-runs the effects for a new film.
  const video = useRef<HTMLVideoElement | null>(null);
  const [clock, setClock] = useState<ClockSource | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState<PlaybackRate>(1);
  const rateRef = useRef<PlaybackRate>(1);
  const [muted, setMuted] = useState(() => !soundWanted());
  const [duration, setDuration] = useState(0);
  const [loop, setLoop] = useState<LoopRange>(NO_LOOP);
  const frameLength = useRef(FALLBACK_FRAME_SECONDS);
  const autoplay = useRef(start.autoplay);
  const opensAt = useRef(start.at);
  const presented = useRef(onFrame);
  useEffect(() => {
    presented.current = onFrame;
  }, [onFrame]);

  // A new film starts from the top, or the first one where it was asked to: autoplay (unless the
  // host waits for the trainer), looped, muted unless sound was chosen before.
  useEffect(() => {
    const element = video.current;
    if (element === null || clock === null) return undefined;
    element.muted = !soundWanted();
    // The speed carries over to the next film; the ref keeps a speed change from restarting this one.
    element.playbackRate = rateRef.current;
    const read = () => {
      clock.set(element.currentTime);
    };
    const onPlay = () => {
      setPlaying(true);
    };
    const onPause = () => {
      setPlaying(false);
      read();
    };
    const onMeta = () => {
      setDuration(Number.isFinite(element.duration) ? element.duration : 0);
    };
    element.addEventListener('play', onPlay);
    element.addEventListener('pause', onPause);
    element.addEventListener('seeked', read);
    element.addEventListener('timeupdate', read);
    element.addEventListener('loadedmetadata', onMeta);
    element.addEventListener('durationchange', onMeta);
    if (opensAt.current !== null) {
      // Before the metadata this is the element's default start position, applied once it lands.
      element.currentTime = opensAt.current;
      opensAt.current = null;
    }
    onMeta();
    read();
    if (autoplay.current) {
      element.play().catch(() => {
        // Autoplay with sound is refused without a gesture; muted it is always allowed.
        element.muted = true;
        setMuted(true);
        void element.play().catch(() => undefined);
      });
    }

    let previous: { mediaTime: number; presentedFrames: number } | null = null;
    let handle = 0;
    const canFrame = 'requestVideoFrameCallback' in element;
    const tick = (_now: number, metadata?: VideoFrameCallbackMetadata) => {
      if (metadata !== undefined) {
        frameLength.current = frameSeconds(previous, metadata, frameLength.current);
        previous = { mediaTime: metadata.mediaTime, presentedFrames: metadata.presentedFrames };
      }
      if (metadata !== undefined || element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        presented.current();
      }
      read();
      handle = canFrame ? element.requestVideoFrameCallback(tick) : requestAnimationFrame(tick);
    };
    handle = canFrame ? element.requestVideoFrameCallback(tick) : requestAnimationFrame(tick);

    return () => {
      if (canFrame) element.cancelVideoFrameCallback(handle);
      else cancelAnimationFrame(handle);
      element.removeEventListener('play', onPlay);
      element.removeEventListener('pause', onPause);
      element.removeEventListener('seeked', read);
      element.removeEventListener('timeupdate', read);
      element.removeEventListener('loadedmetadata', onMeta);
      element.removeEventListener('durationchange', onMeta);
    };
  }, [clock]);

  // The A-B loop: back to A whenever the film passes B, checked on the frames the clock is fed.
  useEffect(() => {
    const element = video.current;
    if (element === null || clock === null || loop.from === null || loop.to === null) {
      return undefined;
    }
    element.loop = false;
    const unsubscribe = clock.subscribe(() => {
      const back = loopedTime(loop, element.currentTime);
      if (back !== null && !element.paused) element.currentTime = back;
    });
    return () => {
      unsubscribe();
      element.loop = true;
    };
  }, [clock, loop]);

  const seekTo = (seconds: number) => {
    const element = video.current;
    if (element === null) return;
    element.currentTime = clampTime(seconds, element.duration);
  };

  const applyRate = (next: PlaybackRate) => {
    setRateState(next);
    rateRef.current = next;
    if (video.current !== null) video.current.playbackRate = next;
  };

  return {
    attach: (next) => {
      video.current = next;
      setClock(next === null ? null : clockOf(next));
    },
    clock,
    currentTime: () => video.current?.currentTime ?? 0,
    playing,
    rate,
    muted,
    duration,
    loop,
    frameSeconds: () => frameLength.current,
    togglePlay: () => {
      const element = video.current;
      if (element === null) return;
      if (element.paused) void element.play().catch(() => undefined);
      else element.pause();
    },
    pause: () => {
      video.current?.pause();
    },
    seekTo,
    seekBy: (delta) => {
      if (video.current !== null) seekTo(video.current.currentTime + delta);
    },
    frame: (by) => {
      const element = video.current;
      if (element === null) return;
      element.pause();
      seekTo(element.currentTime + by * frameLength.current);
    },
    setRate: applyRate,
    stepRate: (by) => {
      applyRate(stepRate(rate, by));
    },
    toggleMute: () => {
      const element = video.current;
      if (element === null) return;
      element.muted = !element.muted;
      setMuted(element.muted);
      rememberSound(!element.muted);
    },
    volumeBy: (delta) => {
      const element = video.current;
      if (element === null) return;
      element.volume = Math.max(0, Math.min(1, element.volume + delta));
      if (element.muted && delta > 0) {
        element.muted = false;
        setMuted(false);
        rememberSound(true);
      }
    },
    loopFrom: () => {
      const at = video.current?.currentTime;
      if (at !== undefined) setLoop((current) => loopFrom(current, at));
    },
    loopTo: () => {
      const at = video.current?.currentTime;
      if (at !== undefined) setLoop((current) => loopTo(current, at));
    },
    loopClear: () => {
      setLoop(NO_LOOP);
    },
  };
}

const NO_SUBSCRIPTION = () => () => undefined;

/**
 * What a leaf prints from the film's clock. `read` turns the seconds into the value the leaf draws —
 * a formatted clock, a frame number — and the leaf re-renders only when that value changes.
 */
export function useVideoClock<T extends string | number>(
  clock: VideoClock | null,
  read: (seconds: number) => T,
): T {
  return useSyncExternalStore(clock?.subscribe ?? NO_SUBSCRIPTION, () => read(clock?.now() ?? 0));
}
